import { Component, Input, Output, EventEmitter, inject, computed, signal, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonContent, IonActionSheet, PopoverController } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { close, share, create, chatbubble, call, globe, logoWhatsapp, logoFacebook, logoInstagram, logoTwitter, logoLinkedin, location, time, business, checkmark, pencil, chevronUp, chevronDown, copy } from 'ionicons/icons';
import { ModalMaterialsSectionComponent } from '../modal-materials-section/modal-materials-section.component';
import { ScheduleComponent } from '../schedule/schedule.component';
import { LeerMasComponent } from '../leer-mas/leer-mas.component';
import { HeaderModalComponent } from '../header-modal/header-modal.component';
import { RecyclingPoint } from '../../../types/recycling-point.interface';
import { CustomModalController } from '../../../shared/services/custom-modal-controller.service';
import { PlatformService } from '../../../shared/services/platform.service';
import { AuthService } from '../../../shared/services/auth.service';
import { PosthogService } from '../../../shared/services/posthog.service';
import { ScreenLayer } from '../../../shared/enums/screen-layer.enum';
import { pointTypesState } from '../../../state/point-types.state';
import { mapEventsState } from '../../../state/map-events.state';
import { SwNavigatorService } from '../../../shared/services/sw-navigator.service';
import { PointDataService } from '../../../shared/services/point-data.service';
import { PopUpComponent } from '../pop-up/pop-up.component';
import type { PopupModel } from '../../../types/popup-model';
import { getAddressFromEntity, extractCity, cleanTextForSearch } from '../../../shared/utils/location.utils';
import { ShareService } from '../../../shared/services/share.service';
import { SocialSectionComponent } from '../social-section/social-section.component';
import { ActionButtonsSliderComponent, ActionButton, MODAL_ACTION_BUTTONS } from '../action-buttons-slider/action-buttons-slider.component';
import { ContactInfoComponent, ContactActionEvent } from '../contact-info/contact-info.component';
import { LinksChipsComponent } from '../links-chips/links-chips.component';
import { TrustSourceBadgeComponent } from '../trust-source-badge/trust-source-badge.component';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { TranslationService } from '../../../shared/services/translation.service';

export interface ModalAction {
  type: 'share' | 'comment' | 'edit' | 'save' | 'contact';
  data?: any;
}

@Component({
  selector: 'app-recycling-point-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonContent,
    IonActionSheet,
    ModalMaterialsSectionComponent,
    ScheduleComponent,
    LeerMasComponent,
    HeaderModalComponent,
    SocialSectionComponent,
    ActionButtonsSliderComponent,
    ContactInfoComponent,
    LinksChipsComponent,
    TrustSourceBadgeComponent,
    TranslatePipe
  ],
  templateUrl: './place-modal.component.html',
  styleUrl: './place-modal.component.scss'
})
export class PlaceModalComponent implements OnChanges, OnInit {
  private modalCtrl = inject(CustomModalController);
  private platform = inject(PlatformService);
  private swnav = inject(SwNavigatorService);
  private pointDataService = inject(PointDataService);
  private authService = inject(AuthService);
  private popoverCtrl = inject(PopoverController);

  private posthog = inject(PosthogService);
  private shareService = inject(ShareService);
  private t = inject(TranslationService);

  // Internal signal for reactive computed properties
  private _point$ = signal<RecyclingPoint | null>(null);

  @Input() point: RecyclingPoint | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['point']) {
      this._point$.set(changes['point'].currentValue);
      // Also reset edited point when point changes
      if (this.point) {
        this.resetEditedPoint();
      }
    }
  }

  @Input() scheduleDays: { code: string; label: string; isOpen: boolean }[] = [];
  @Input() scheduleHoursText: string = '';
  @Input() initialHeight?: number;

  @Output() modalAction = new EventEmitter<ModalAction>();
  @Output() dismiss = new EventEmitter<void>();
  @Output() repointMap = new EventEmitter<{ lat: number; lng: number }>();

  // Input materials (what the point receives)
  readonly inputMaterials = computed(() => {
    const point = this._point$();
    if (!point) return [];
    if (point.materialInput && Array.isArray(point.materialInput) && point.materialInput.length > 0) {
      return point.materialInput;
    }
    if (point.materials && Array.isArray(point.materials) && point.materials.length > 0) {
      return point.materials;
    }
    return [];
  });

  // Output materials (what the point delivers)
  readonly outputMaterials = computed(() => {
    const point = this._point$();
    if (!point) return [];
    if (point.materialOutput && Array.isArray(point.materialOutput) && point.materialOutput.length > 0) {
      return point.materialOutput;
    }
    return [];
  });

  // Address string computed from point
  readonly addressString = computed(() => {
    const point = this._point$();
    return getAddressFromEntity(point, { excludeCities: [] });
  });

  // Show edit button when place is missing materials, address, or schedule
  readonly showEditButton = computed(() => {
    const point = this._point$();
    if (!point) return false;
    const hasMaterials = (point.materialInput?.length || 0) > 0
      || (point.materials?.length || 0) > 0;
    const hasAddress = !!(point.address?.street || point.address?.fullAddress);
    const hasSchedule = !!(point.schedules && point.schedules.length > 0);
    return !hasMaterials || !hasAddress || !hasSchedule;
  });

  // Static method to open modal
  /*   static async openModal(
      modalCtrl: CustomModalController,
      point: RecyclingPoint,
      scheduleDays: { code: string; label: string; isOpen: boolean }[] = [],
      scheduleHoursText: string = ''
    ): Promise<HTMLIonModalElement> {
      const modal = await modalCtrl.create({
        component: PlaceModalComponent,
        componentProps: {
          point,
          scheduleDays,
          scheduleHoursText
        },
        cssClass: 'recycling-point-modal-class'
      });
  
      await modal.present();
      return modal;
    } */

  // Action buttons for slider
  actionButtons: ActionButton[] = [...MODAL_ACTION_BUTTONS];

  // Edit mode state
  isEditMode: boolean = false;
  editedPoint!: RecyclingPoint;

  // Action sheet state
  isActionSheetOpen: boolean = false;
  actionSheetButtons = [
    {
      text: this.t.instant('placeModal.shareLocation'),
      icon: 'location',
      handler: () => this.shareLocation()
    },
    {
      text: this.t.instant('placeModal.shareInfo'),
      icon: 'business',
      handler: () => this.shareInfo()
    },
    {
      text: this.t.instant('placeModal.copyAddress'),
      icon: 'copy',
      handler: () => this.copyAddress()
    },
    {
      text: this.t.instant('common.cancel'),
      role: 'cancel'
    }
  ];

  constructor() {
    addIcons({
      close,
      share,
      create,
      chatbubble,
      call,
      globe,
      logoWhatsapp,
      logoFacebook,
      logoInstagram,
      logoTwitter,
      logoLinkedin,
      location,
      time,
      business,
      checkmark,
      pencil,
      chevronUp,
      chevronDown,
      copy
    });
    // Track modal open
    setTimeout(() => {
      this.posthog.init();
      this.posthog.capture('modal_open', { modal: 'recycling_point', pointId: this.point?.id });
    }, 0);
  }

  ngOnInit() {
    // Ensure signal is populated for dynamically created components (Ionic modals)
    // where componentProps might not trigger ngOnChanges on first render
    if (this.point && !this._point$()) {
      this._point$.set(this.point);
    }
    this.resetEditedPoint();
  }

  private resetEditedPoint() {
    if (this.point) {
      this.editedPoint = { ...this.point };
    }
  }

  // Modal actions
  closeModal() {
    this.posthog.capture('modal_close', { modal: 'recycling_point', pointId: this.point?.id });
    this.modalCtrl.dismiss(null, 'close');
  }

  toggleModalHeight() {
    this.posthog.capture('modal_toggle_height', { modal: 'recycling_point', pointId: this.point?.id });
    this.modalCtrl.toggleHeight();
  }

  repointMapOnPoint(event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (!this.point?.position) return;

    console.log('🗺️ Repoint button clicked, requesting map repoint via global state');

    // Use global state instead of event emitter
    mapEventsState.requestRecenter({
      lat: this.point.position.lat,
      lng: this.point.position.lng
    });

    this.posthog.capture('repoint_map', { pointId: this.point?.id });
  }

  async suggestEdit() {
    if (!this.point) return;

    this.posthog.capture('suggest_edit', { pointId: this.point?.id });

    // Check if user is authenticated
    /*     if (!this.authService.isAuthenticated()) {
            
          await this.showLoginPopup();
          return;
         
         //DEV COMMENTED DEVCODE
        }   */

    // Set point data in shared service for edit component
    this.pointDataService.setPointForEdit(this.point);

    // Close the modal first
    // this.closeModal();

    // Navigate to edit page with explicit back path to home
    // this.swnav.navigateObj({ path: '/edit-point', back_path: '/home', context: 2, typeTransition: 2 });
    this.swnav.navigateObj({ path: `/edit-point/${this.point.id}`, back_path: '/home', layer: ScreenLayer.Edit, typeTransition: 1 });
  }

  /**
   * Show popup to prompt user to login
   */
  private async showLoginPopup(): Promise<void> {
    const popupData: PopupModel = {
      titleh2: this.t.instant('placeModal.loginTitle'),
      bodyText: this.t.instant('placeModal.loginBody'),
      btn1: this.t.instant('placeModal.loginBtn'),
      btn2: this.t.instant('common.cancel'),
      btn1Style: 1, // Primary style button
      icon: 'person.svg',
      iconSize: 2, // Medium size
      behavior: 2 // Normal behavior
    };

    const popover = await this.popoverCtrl.create({
      component: PopUpComponent,
      componentProps: {
        popupInfo: popupData
      },
      cssClass: 'custom-popover',
      backdropDismiss: false
    });

    await popover.present();

    const { data } = await popover.onDidDismiss();

    // If user clicked "Iniciar sesión" (btn1)
    if (data === 1) {
      // Navigate to profile page where Google auth is available
      this.swnav.navigateObj({ path: '/profile', back_path: '/home', layer: ScreenLayer.Edit, closeModals: true });
    }
  }

  toggleEditMode() {
    if (this.isEditMode) {
      // Save changes
      this.saveChanges();
    } else {
      // Enter edit mode
      this.isEditMode = true;
    }
  }

  cancelEdit() {
    this.isEditMode = false;
    this.resetEditedPoint();
  }

  saveChanges() {
    this.isEditMode = false;
    this.modalAction.emit({
      type: 'save',
      data: this.editedPoint
    });
  }

  // Share functionality
  openShareOptions() {
    this.posthog.capture('open_share_options', { pointId: this.point?.id });
    this.isActionSheetOpen = true;
  }

  shareLocation() {
    if (!this.point) return;
    const entity = this.shareService.fromRecyclingPoint(this.point);
    this.shareService.shareLocation(entity);

    this.modalAction.emit({
      type: 'share',
      data: { type: 'location', point: this.point }
    });
    this.posthog.capture('share_location', { pointId: this.point?.id });
  }

  shareInfo() {
    if (!this.point) return;
    const entity = this.shareService.fromRecyclingPoint(this.point);
    this.shareService.shareInfo(entity);

    this.modalAction.emit({
      type: 'share',
      data: { type: 'info', point: this.point }
    });
    this.posthog.capture('share_info', { pointId: this.point?.id });
  }

  copyAddress() {
    if (!this.point) return;
    const entity = this.shareService.fromRecyclingPoint(this.point);
    this.shareService.copyToClipboard(this.shareService.getAddressString(entity));
    this.posthog.capture('copy_address', { pointId: this.point?.id });
  }

  // Contact actions
  callPhone() {
    if (!this.point || !this.point.telefono) return;

    this.posthog.capture('call_phone', { pointId: this.point?.id, phone: this.point.telefono });
    if (this.platform.window) {
      this.platform.window.location.href = `tel:${this.point.telefono}`;
    }
    this.modalAction.emit({
      type: 'contact',
      data: { type: 'phone', value: this.point.telefono }
    });
  }

  openWhatsApp() {
    if (!this.point || !this.point.telefono) return;

    const cleanPhone = this.point.telefono.replace(/\D/g, '');
    let formattedPhone = cleanPhone;
    if (!formattedPhone.startsWith('54') && formattedPhone.length >= 10) {
      formattedPhone = '54' + formattedPhone;
    }

    const whatsappUrl = `https://wa.me/${formattedPhone}`;
    this.platform.windowOpen(whatsappUrl, '_blank');

    this.posthog.capture('open_whatsapp', { pointId: this.point?.id, phone: this.point.telefono });
    this.modalAction.emit({
      type: 'contact',
      data: { type: 'whatsapp', value: this.point.telefono }
    });
  }

  openWebsite() {
    if (!this.point || !this.point.sitio_web) return;

    const website = this.point.sitio_web.startsWith('http') ? this.point.sitio_web : `https://${this.point.sitio_web}`;
    this.platform.windowOpen(website, '_blank');

    this.posthog.capture('open_website', { pointId: this.point?.id, website: this.point.sitio_web });
    this.modalAction.emit({
      type: 'contact',
      data: { type: 'website', value: this.point.sitio_web }
    });
  }

  openSocialMedia(platform: string, url: string) {
    if (url) {
      const socialUrl = url.startsWith('http') ? url : `https://${url}`;
      this.platform.windowOpen(socialUrl, '_blank');

      this.posthog.capture('open_social_media', { pointId: this.point?.id, platform, url });
      this.modalAction.emit({
        type: 'contact',
        data: { type: platform, value: url }
      });
    }
  }

  searchCompanyOnGoogle() {
    if (!this.point || !this.point.name) return;
    // return; // HARD CODED DEV CODE
    try {
      const city = this.extractCityFromAddress() || 'Rosario Argentina';
      const cleanCompanyName = cleanTextForSearch(this.point!.name);

      if (!cleanCompanyName) return;

      const searchQuery = encodeURIComponent(`${cleanCompanyName} ${city}`);
      const googleSearchUrl = `https://www.google.com/search?q=${searchQuery}`;

      this.platform.windowOpen(googleSearchUrl, '_blank');

      this.posthog.capture('search_company_on_google', { pointId: this.point?.id, query: `${cleanCompanyName} ${city}` });
      this.modalAction.emit({
        type: 'contact',
        data: { type: 'google_search', value: `${cleanCompanyName} ${city}` }
      });
    } catch (error) {
      console.error('Error opening Google search:', error);
    }
  }

  // DEPRECATED: Use addressString() computed signal instead
  getAddressString(): string {
    return this.addressString();
  }

  // DEPRECATED: Use showEditButton() computed signal instead
  shouldShowEditButton(): boolean {
    return this.showEditButton();
  }

  getCleanAddress(address: string): string {
    // Deprecated - use formatAddress from location.utils instead
    return getAddressFromEntity({ address });
  }

  private extractCityFromAddress(): string | null {
    if (!this.point?.address) return null;
    return extractCity(this.point.address) || 'Rosario Argentina';
  }

  // Entity type methods - using 'type' field (source of truth from API)
  getEntityType(): string {
    if (!this.point) return 'unknown';
    // Use 'type' field as source of truth
    return this.point.type || 'unknown';
  }

  shouldShowEntityType(): boolean {
    return this.getEntityType() !== 'unknown';
  }

  getEntityTypeLabel(): string {
    const pointType = this.getEntityType();
    if (pointType === 'unknown') return '';

    // Use global state to get the display name
    return pointTypesState.getTypeName(pointType);
  }

  getFacilityTypeLabel(): string {
    // Facility type is no longer supported - return empty
    return '';
  }

  shouldShowGoogleSearch(): boolean {
    if (!this.point) return false;
    // Show Google search for any point without a website
    return !this.point.sitio_web;
  }

  hasContactInfo(): boolean {
    if (!this.point) return false;

    return !!(this.point.sitio_web ||
      this.point.telefono ||
      this.point.socialMedia?.facebook ||
      this.point.socialMedia?.instagram ||
      this.point.socialMedia?.twitter ||
      this.point.socialMedia?.linkedin);
  }

  /** Handle contact action from unified contact-info component */
  onContactAction(event: ContactActionEvent) {
    switch (event.type) {
      case 'website':
        this.openWebsite();
        break;
      case 'phone':
        this.callPhone();
        break;
      case 'whatsapp':
        this.openWhatsApp();
        break;
      case 'share':
        this.shareInfo();
        break;
      case 'social':
        if (event.platform && event.value) {
          this.openSocialMedia(event.platform, event.value);
        }
        break;
    }
  }

  formatDate(date: Date): string {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return 'ayer';
    } else if (diffDays < 7) {
      return `hace ${diffDays} días`;
    } else {
      return date.toLocaleDateString('es-AR');
    }
  }

  // Helper methods for social media
  getFacebookUrl(): string | null {
    return this.point?.socialMedia?.facebook || null;
  }

  getInstagramUrl(): string | null {
    return this.point?.socialMedia?.instagram || null;
  }

  getTwitterUrl(): string | null {
    return this.point?.socialMedia?.twitter || null;
  }

  getLinkedinUrl(): string | null {
    return this.point?.socialMedia?.linkedin || null;
  }

  /** Handle action slider button clicks */
  onActionButtonClick(actionId: string) {
    switch (actionId) {
      case 'share':
        this.shareInfo();
        break;
      case 'google':
        this.searchCompanyOnGoogle();
        break;
      case 'edit':
        this.suggestEdit();
        break;
      case 'report':
        this.scrollToReports();
        break;
      case 'save':
        this.toggleSave();
        break;
    }
  }

  /** Scroll to the social/report section and highlight it */
  private scrollToReports() {
    const el = document.querySelector('.status-report-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Apply ui-guide highlight after scroll settles
      setTimeout(() => {
        el.classList.add('ui-guide');
        el.addEventListener('animationend', () => {
          el.classList.add('ui-guide-out');
          setTimeout(() => {
            el.classList.remove('ui-guide', 'ui-guide-out');
          }, 300);
        }, { once: true });
      }, 400);
    }
    this.posthog.capture('action_report_click', { pointId: this.point?.id });
  }

  /** Toggle save/bookmark (placeholder - emits event for parent) */
  private toggleSave() {
    const saveBtn = this.actionButtons.find(b => b.id === 'save');
    if (saveBtn) {
      saveBtn.active = !saveBtn.active;
    }
    this.modalAction.emit({ type: 'save', data: { pointId: this.point?.id, saved: saveBtn?.active } });
    this.posthog.capture('action_save_toggle', { pointId: this.point?.id, saved: saveBtn?.active });
  }
}
